import { object, string, boolean, ref, date, mixed } from "yup";

import {
  AssetType,
  AssetPurchaseType,
  AssetJoint,
  QueryScopeType,
} from "@interfaces/index";

export const changePasswordSchema = object({
  currentPassword: string()
    .required("The Password field is required.")
    .max(255, "The Password field can't be more than 255 symbols."),
  password: string()
    .required("The New password field is required.")
    .max(255, "The New password field can't be more than 255 symbols.")
    .min(8, "The New password field must be at least 8 characters.")
    .matches(
      /[a-z]+/,
      "The New password field must contain at least one lowercase letter.",
    )
    .matches(
      /[A-Z]+/,
      "The New password field must contain at least one uppercase letter.",
    )
    .matches(
      /[0-9]+/,
      "The New password field must contain at least one number.",
    ),
  confirmPassword: string()
    .required("The Confirm password field is required.")
    .max(255, "The Confirm password field can't be more than 255 symbols.")
    .oneOf(
      [ref("password")],
      "The password field confirmation does not match.",
    ),
});

export const acceptInvitationSchema = object({
  email: string().max(255).required(),
  password: string()
    .required("The Password field is required.")
    .max(255, "The Password field can't be more than 255 symbols.")
    .min(8, "The Password field must be at least 8 characters.")
    .matches(
      /[a-z]+/,
      "The password field must contain at least one lowercase letter.",
    )
    .matches(
      /[A-Z]+/,
      "The password field must contain at least one uppercase letter.",
    )
    .matches(/[0-9]+/, "The password field must contain at least one number."),
  confirmPassword: string()
    .required("The Confirm password field is required.")
    .max(255, "The Confirm password field can't be more than 255 symbols.")
    .oneOf(
      [ref("password")],
      "The password field confirmation does not match.",
    ),
  code: string().max(255).required(),
});

export const loginSchema = object({
  email: string()
    .required("The Email field is required.")
    .max(255, "The Email field can't be more than 255 symbols.")
    .email("The Email field must be a valid email address."),
  password: string()
    .required("The Password field is required.")
    .max(255, "The Password field can't be more than 255 symbols.")
    .min(8, "The Password field must be at least 8 characters.")
    .matches(
      /[a-z]+/,
      "The password field must contain at least one lowercase letter.",
    )
    .matches(
      /[A-Z]+/,
      "The password field must contain at least one uppercase letter.",
    )
    .matches(/[0-9]+/, "The password field must contain at least one number."),
});

export const passwordResetSchema = object({
  email: string().max(255).required(),
  password: string()
    .required("The Password field is required.")
    .max(255, "The Password field can't be more than 255 symbols.")
    .min(8, "The Password field must be at least 8 characters.")
    .matches(
      /[a-z]+/,
      "The password field must contain at least one lowercase letter.",
    )
    .matches(
      /[A-Z]+/,
      "The password field must contain at least one uppercase letter.",
    )
    .matches(/[0-9]+/, "The password field must contain at least one number."),
  confirmPassword: string()
    .required("The Confirm password field is required.")
    .max(255, "The Confirm password field can't be more than 255 symbols.")
    .oneOf(
      [ref("password")],
      "The password field confirmation does not match.",
    ),
  code: string().max(255).required(),
});

export const leaveSchema = object({
  message: string().required("The Message field is required."),
});

export const changeNameSchema = object({
  firstName: string()
    .required("The Firstname field is required.")
    .max(255, "The Firstname field can't be more than 255 symbols."),
  lastName: string()
    .required("The Lastname field is required.")
    .max(255, "The Lastname field can't be more than 255 symbols."),
});

export const changePhoneSchema = object({
  phone: string()
    .required("The Phone field is required.")
    .min(10, "The Phone field must contain minimum 10 symbols.")
    .max(13, "The Phone field must contain maximum 13 symbols.")
    .matches(/^[+\d]{1}[\d]{0,12}$/, "The Phone field has invalid format."),
});

export const passwordForgotSchema = object({
  email: string()
    .required("The Email field is required.")
    .max(255, "The Email field can't be more than 255 symbols.")
    .email("The Email field must be a valid email address."),
});

export const registerSchema = object({
  firstName: string()
    .required("The First name field is required.")
    .max(255, "The First name field can't be more than 255 symbols."),
  lastName: string()
    .required("The Last name field is required.")
    .max(255, "The Last name field can't be more than 255 symbols."),
  email: string()
    .required("The Email field is required.")
    .max(255, "The Email field can't be more than 255 symbols.")
    .email("The Email field must be a valid email address."),
  phone: string()
    .required("The Phone field is required.")
    .min(10, "The Phone field must contain minimum 10 symbols.")
    .max(13, "The Phone field must contain maximum 13 symbols.")
    .matches(/^[+\d]{1}[\d]{0,12}$/, "The Phone field has invalid format."),
  businessName: string()
    .required("The Business description field is required.")
    .max(255, "The Business description field can't be more than 255 symbols."),
});

export const transactionSchema = object({
  businessId: string()
    .required("The Business field is required.")
    .max(255, "The Business field can't be more than 255 symbols."),
  categoryId: string()
    .required("The Category field is required.")
    .max(255, "The Category field can't be more than 255 symbols."),
  isAssetRequired: boolean(),
  assetId: string().when(["isAssetRequired"], (fields, schema) => {
    if (fields[0]) {
      return schema.required("The Asset field is required.");
    }
    return schema;
  }),
  date: date().required("The Date field is required."),
  amount: string().required("The Amount field is required."),
  isDescriptionRequired: boolean(),
  description: string().when(["isDescriptionRequired"], (fields, schema) => {
    if (fields[0]) {
      return schema.required("The Description field is required.");
    }
    return schema;
  }),
});

export const assetSchema = object({
  businessId: string()
    .required("The Business field is required.")
    .max(255, "The Business field can't be more than 255 symbols."),
  type: mixed<AssetType | "">()
    .required("The Asset type field is required.")
    .oneOf(
      ["vehicle", "rental_property", "other"],
      "The Asset type field is required.",
    ),
  purchaseType: mixed<AssetPurchaseType | "">()
    .required("The Purchase type field is required.")
    .oneOf(
      [
        "purchased_outright",
        "hire_purchase",
        "personal_contract_purchase",
        "loan",
        "lease",
        "mortgage",
        "finance",
      ],
      "The Purchase type field is required.",
    ),
  name: string()
    .required("The Name field is required.")
    .max(255, "The Name field can't be more than 255 symbols."),
  date: date().required("The Date field is required."),
  cost: string().when(
    ["businessType", "type", "purchaseType"],
    (fields, schema) => {
      if (
        !(fields[0] === "landlord" && fields[1] === "vehicle") &&
        fields[1] !== "rental_property" &&
        fields[2] !== "lease"
      ) {
        return schema.required("The Cost field is required.");
      }
      return schema;
    },
  ),
  joint: mixed<AssetJoint>()
    .oneOf(["no", "yes"], "The Joint field has incorrect value.")
    .when(["type"], (fields, schema) => {
      if (fields[0] === "rental_property") {
        return schema.required("The Joint field is required.");
      }
      return schema;
    }),
  jointShare: string().when(["joint"], (fields, schema) => {
    if (fields[0] === "yes") {
      return schema.required("The Joint share field is required.");
    }
    return schema;
  }),
  comment: string()
    .max(255, "The Comment field can't be more than 255 symbols.")
    .optional(),
  notApplicableAgreement: boolean(),
  uploadLaterAgreement: boolean(),
  notApplicableInvoice: boolean(),
  uploadLaterInvoice: boolean(),
  isDefault: boolean(),
});

export const assetDisposalSchema = object({
  date: date().required("The Date field is required."),
});

export const mileageSchema = object({
  businessId: string()
    .required("The Business field is required.")
    .max(255, "The Business field can't be more than 255 symbols."),
  assetId: string()
    .required("The Asset field is required.")
    .max(255, "The Asset field can't be more than 255 symbols."),
  dateFrom: date()
    .typeError("The Date From and Date To fields are required.")
    .required("The Date From and Date To fields are required."),
  dateTo: date()
    .typeError("The Date From and Date To fields are required.")
    .required("The Date From and Date To fields are required."),
  businessMiles: string()
    .required("The Business Miles field is required.")
    .max(255, "The Business Miles field can't be more than 255 symbols."),
  isDiBusiness: boolean(),
  privateMiles: string().when(["isDiBusiness"], (fields, schema) => {
    if (fields[0]) {
      return schema.required("The Private Miles field is required.");
    }
    return schema;
  }),
  comments: string()
    .max(255, "The Comments field can't be more than 255 symbols.")
    .optional(),
});

export const querySoftwareSchema = object({
  phone: string()
    .required("The Phone field is required.")
    .min(10, "The Phone field must contain minimum 10 symbols.")
    .max(13, "The Phone field must contain maximum 13 symbols.")
    .matches(/^[+\d]{1}[\d]{0,12}$/, "The Phone field has invalid format."),
  message: string().required("The Message field is required."),
  userId: string().required("The User ID field is required."),
  browserVersion: string().required("The Browser Version field is required."),
});

export const queryAccountSubmissionSchema = object({
  category: mixed<QueryScopeType>()
    .required("The Category type field is required.")
    .oneOf(["account", "submission"], "The Category type field is required."),
  topicId: string().required("The Topic field is required."),
  submissionId: string().when(["category"], (fields, schema) => {
    if (fields[0] === "submission") {
      return schema.required("The Submission field is required.");
    }
    return schema;
  }),
  subject: string().required("The Subject field is required."),
  message: string().required("The Message field is required."),
});
